import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash,FaArrowLeft,FaArrowRight, FaHome, FaBuilding, FaCity, FaPlus, FaEye, FaTimes, FaExpand, FaCalendar, FaMapMarker, FaBriefcase } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from "../../../services/axiosInstance"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faLocationDot,
  faRulerCombined,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

const PortfolioList = () => {
  const [activeImage, setActiveImage] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [imagePreviews, setImagePreviews] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchPortfolios();
  }, []);

  
  const getProjectIcon = (project) => {
    const title = project.title?.toLowerCase() || '';
    
    if (title.includes('villa') || title.includes('résidence') || title.includes('maison')) {
      return <FaHome className="text-white text-6xl" />;
    } else if (title.includes('commercial') || title.includes('centre') || title.includes('bureau')) {
      return <FaBuilding className="text-white text-6xl" />;
    } else if (title.includes('industriel') || title.includes('usine') || title.includes('entrepôt')) {
      return <FaCity className="text-white text-6xl" />;
    } else {
      return <FaBuilding className="text-white text-6xl" />;
    }
  };

  const getProjectGradient = (project, index) => {
    const gradients = [
      'from-blue-500 to-purple-600',
      'from-green-500 to-teal-600',
      'from-orange-500 to-red-600',
      'from-pink-500 to-rose-600',
      'from-indigo-500 to-blue-600',
      'from-yellow-500 to-orange-600'
    ];
    
    return gradients[index % gradients.length];
  };

 const showProjectDetails = (projectId) => {
  const project = portfolios.find(p => p.id === projectId);
  setSelectedProject(project);
  setActiveImage(project.images?.[0] || null);
};


  const handleMouseEnter = (projectId) => {
    setHoveredProject(projectId);
  };

  const handleMouseLeave = () => {
    setHoveredProject(null);
  };

  const fetchPortfolios = async () => {
  try {
    const response = await api.get("/portfolio");

    const data = response.data;
    setPortfolios(data);

    // Preload first 4 images per project
    const previews = {};
    data.forEach((project) => {
      if (project.images && project.images.length > 0) {
        previews[project.id] = project.images.slice(0, 4);
      }
    });

    setImagePreviews(previews);
  } catch (error) {
    setMessage("Erreur: " + (error.response?.data?.message || error.message));
  } finally {
    setLoading(false);
  }
}; 

  const closeModal = () => {
    setSelectedProject(null);
  };

  const handleAddNew = () => {
    navigate('/add-portfolio');
  };

  if (loading) {
    return (
      <div id="portfolio" className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des projets...</p>
        </div>
      </div>
    );
  }

  return (
    <div id="portfolio" className="min-h-screen bg-gray-50 py-8 mt-12">
      <div className="max-w-7xl mx-auto px-4">
  {/* Header */}
  <div className="text-center mb-12">
    <h1 className="text-4xl font-bold text-gray-900">Portfolio Ctrl+</h1>
  </div>

  {/* Message Alert */}
  {message && (
    <div className={`mb-8 p-4 rounded-lg border text-sm ${
      message.includes('Erreur') 
        ? 'bg-red-50 text-red-700 border-red-200' 
        : 'bg-green-50 text-green-700 border-green-200'
    }`}>
      {message}
    </div>
  )}

  {/* Portfolio Grid */}
  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
    {portfolios.map((portfolio) => (
      <article
        key={portfolio.id}
        onMouseEnter={() => handleMouseEnter(portfolio.id)}
        onMouseLeave={handleMouseLeave}
        className="group bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
      >
        {/* Image */}
        {portfolio.images?.length > 0 && (
          <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
            <img
              src={`${process.env.REACT_APP_API_URL}${portfolio.images[0]}`}
              alt={portfolio.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            
            {/* Hover Overlay */}
            {hoveredProject === portfolio.id && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <button
                  onClick={() => showProjectDetails(portfolio.id)}
                  className="px-5 py-2 bg-emerald-600 text-gray-300 text-sm rounded-lg font-medium hover:bg-emerald-800"
                >
                  Aperçu
                </button>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {portfolio.title}
          </h3>
          
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {portfolio.description}
          </p>

          {/* Details */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Type</span>
              <span className="font-medium">{portfolio.project_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Localisation</span>
              <span className="font-medium">{portfolio.location}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Date</span>
              <span className="font-medium">{portfolio.date}</span>
            </div>
          </div>
        </div>
      </article>
    ))}
  </div>

  {/* Empty State */}
  {portfolios.length === 0 && (
    <div className="text-center py-16">
      <div className="text-5xl text-gray-300 mb-4">📁</div>
      <h3 className="text-lg font-medium text-gray-700 mb-2">
        Aucun projet
      </h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        Commencez par ajouter votre premier projet
      </p>
      <button
        onClick={handleAddNew}
        className="px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
      >
        Ajouter un Projet
      </button>
    </div>
  )}
</div>

      {/* Project Details Modal */}
      {selectedProject && (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    {/* Light backdrop */}
    <div className="absolute inset-0 bg-white/80 backdrop-blur-2xl" />

    {/* Modal */}
    <div className="relative w-full max-w-6xl h-[95vh] bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col">

      {/* Close button */}
      <button
        onClick={closeModal}
        className="absolute top-6 right-6 z-20 w-11 h-11 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition"
      >
        ✕
      </button>

      {/* HERO IMAGE */}
      <div className="relative h-[380px] bg-gray-50 border-b border-gray-200 flex items-center justify-center">
        {activeImage && (
  <img
  key={activeImage} 
    src={`${process.env.REACT_APP_API_URL}${activeImage}`}
    alt={selectedProject.title}
    className="w-full h-full object-contain transition-all duration-300"
  />
)}


        {/* Image thumbnails */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3 bg-white/80 backdrop-blur-md px-4 py-2 rounded-xl border border-gray-200">
          {selectedProject.images.slice(0, 4).map((image, index) => (
  <div
    key={index}
    onClick={() => setActiveImage(image)}
    className={`
      w-16 h-16 rounded-lg overflow-hidden border cursor-pointer
      transition transform hover:scale-105
      ${activeImage === image ? "ring-2 ring-emerald-500" : "border-gray-300"}
    `}
  >
    <img
      src={`${process.env.REACT_APP_API_URL}${image}`}
      alt=""
      className="w-full h-full object-contain bg-white"
    />
  </div>
))}

        </div>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto px-10 py-8">
        {/* Title */}
        <div className="mb-3">
          <span className="inline-block px-4 py-1 rounded-full bg-blue-50 text-blue-700 text-sm mb-3">
            Project Showcase
          </span>
          <h2 className="text-4xl font-bold text-gray-900 ">
            {selectedProject.title}
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Description */}
          <div className="lg:col-span-2 space-y-8">
              <p className="text-gray-700 leading-relaxed text-lg">
                {selectedProject.full_description}
              </p>

            {/* Stats */}
           <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
  {[
    { label: "Timeline", value: selectedProject.date, icon: faCalendarDays, color: "text-blue-600 bg-blue-50" },
    { label: "Location", value: selectedProject.location, icon: faLocationDot, color: "text-emerald-600 bg-emerald-50" },
    { label: "Scale", value: selectedProject.surface, icon: faRulerCombined, color: "text-purple-600 bg-purple-50" },
    { label: "Duration", value: selectedProject.duration, icon: faClock, color: "text-amber-600 bg-amber-50" },
  ].map(({ label, value, icon, color }) => (
    <div
      key={label}
      className="bg-white rounded-2xl p-5 border border-gray-200 flex items-center gap-3"
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
        <FontAwesomeIcon icon={icon} className="text-lg" />
      </div>
      <div>
        <div className="text-sm text-gray-400 mb-1">{label}</div>
        <div className="font-semibold text-gray-900">{value}</div>
      </div>
    </div>
  ))}
</div>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default PortfolioList;